import React from "react";
import { ContractCompleteCard } from "../cards/ContractCompleteCard";
import { ContractCancelledCard } from "../cards/ContractCancelledCard";
import { MessageLayout } from "../layout/MessageLayout";
import { MessageCardWrapper } from "../layout/MessageCardWrapper";
import { cn, Skeleton } from "@semo/shared-ui";
import { useChatActions } from "../../context/ChatActionContext";
import type { ChatMessageProps } from "../types";
import type { ContractMessageResponseDto } from "@semo/api-contracts";
import { ContractInfoDtoStatus } from "@semo/api-contracts";
import type { LocalMessageState } from "../../types/message";

interface ContractMessageProps extends Omit<ChatMessageProps, "message"> {
  message: ContractMessageResponseDto & LocalMessageState;
  sender: "me" | "other";
  time: string;
}

export const ContractMessage: React.FC<ContractMessageProps> = ({
  message,
  messageId,
  sender,
  partyName,
  time,
  amount,
  showAvatar = true,
  showName = true,
}) => {
  const { isRead, createdAt, isPending, isError } = message;
  const { onViewContract, onPrintReceipt, onRetry, onOpenCmarketContracts } =
    useChatActions();

  if (!message.contractInfo) return null;

  const { status, totalAmount, contractId } = message.contractInfo;
  const tid = message.contractInfo.tid; // Extract tid separately to avoid shadowing or just for clarity
  const displayAmount = totalAmount
    ? `${totalAmount.toLocaleString("ko-KR")}원`
    : amount;

  // Show skeleton UI while loading or pending status
  if (isPending || status === ContractInfoDtoStatus.pending) {
    return (
      <MessageLayout
        sender={sender}
        partyName={partyName}
        time={time}
        isRead={isRead}
        isPending={isPending}
        isError={isError}
        showAvatar={showAvatar}
        showName={showName}
        variant="card"
      >
        <MessageCardWrapper
          data-slot="chat-contract-skeleton"
          className={cn(
            "bg-white ",
            "border rounded-xl p-3 sm:p-4 shadow-sm",
            sender === "me" ? "border-primary/30 " : "border-slate-200 ",
          )}
        >
          <div className="flex items-center gap-2 sm:gap-3 mb-2 sm:mb-3 pb-2 sm:pb-3 border-b border-slate-100 ">
            <Skeleton className="w-8 h-8 sm:w-10 sm:h-10 rounded-lg" />
            <div className="flex-1 space-y-2">
              <Skeleton className="h-4 w-32" />
            </div>
          </div>
          <div className="space-y-2">
            <Skeleton className="h-16 w-full rounded-lg" />
          </div>
        </MessageCardWrapper>
      </MessageLayout>
    );
  }

  return (
    <MessageLayout
      sender={sender}
      partyName={partyName}
      time={time}
      isRead={isRead}
      isPending={isPending}
      isError={isError}
      showAvatar={showAvatar}
      showName={showName}
      variant="card"
      // ContractCompleteCard 만 document 폭(max-w-md)을 쓴다. 본문 칼럼 상한을
      // 카드와 맞춰야 시간/읽음이 카드에 붙는다 (MessageLayout 주석 참고).
      cardSize={
        status === ContractInfoDtoStatus.complete && contractId
          ? "document"
          : "default"
      }
      onRetry={() => onRetry?.(message.id)}
    >
      {status === ContractInfoDtoStatus.complete && contractId ? (
        <ContractCompleteCard
          message={message}
          amount={displayAmount || "0원"}
          contractId={contractId}
          sender={sender}
          paymentMethod={message.contractInfo?.paymentMethod}
          onViewContract={(id) => onViewContract?.(id)}
          onPrintReceipt={
            onPrintReceipt ? () => onPrintReceipt(messageId!) : undefined
          }
          // 계약 시 고른 현금/카드가 액션을 가른다. 미상(레거시)이면 카드가 섹션을 안 그린다.
          // 공급사 관점이나 임베드 밖이면 onOpenCmarketContracts 가 미주입이라 **버튼만**
          // 빠진다 — 아래 계산서/결제 진행 상태는 양쪽 다 본다.
          paymentChannel={message.contractInfo?.paymentChannel ?? null}
          settlesOnCmarket={message.contractInfo?.settlesOnCmarket ?? true}
          isPaid={Boolean(message.contractInfo?.paidAt)}
          // 계산서 진행 상태. 백엔드가 계약을 그때그때 읽어 채운다(스냅샷 아님).
          taxInvoiceStatus={message.contractInfo?.taxInvoiceStatus ?? null}
          onOpenCmarketContracts={onOpenCmarketContracts}
        />
      ) : status === ContractInfoDtoStatus.cancelled ? (
        <ContractCancelledCard
          message={{ createdAt }}
          sender={sender}
          tid={tid}
        />
      ) : null}
    </MessageLayout>
  );
};
